import {
  TASK_CLOSE_NO_COMMENT_CONFIRM,
  taskHasUserComment,
  type AllocationMonthContextDto,
  type CopyLastMonthTasksResponseDto,
  type CreateTaskDto,
  type DuplicateTaskDto,
  type TaskCommentDto,
  type TaskDto,
  type UpdateTaskDto,
} from '@schwrz/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api, apiDeleteKeepalive } from '../shared/api/client';
import { projectApiParam } from '../shared/lib/project-route';
import { insertTaskIdSorted } from '../shared/lib/taskOrder';
import { showToast, dismissToast } from '../shared/lib/toast';

import { useCommentsStore } from './comments';
import { useProjectsStore } from './projects';

export const useTasksStore = defineStore('tasks', () => {
  /** Authoritative task records — the only place task data lives. */
  const byId = ref(new Map<string, TaskDto>());
  /** projectId → ordered task ids (server list order when bulk-loaded). */
  const projectIndex = ref(new Map<string, string[]>());
  /** ownerId → ordered task ids (assignee or co-assignee). */
  const ownerIndex = ref(new Map<string, string[]>());

  const loadingProjects = ref(new Set<string>());
  const loadingOwners = ref(new Set<string>());

  const allocationMonthContext = ref<AllocationMonthContextDto | null>(null);

  /** Read-only view: projectId → resolved tasks (backward compat for direct map reads). */
  const byProject = computed(() => {
    const map = new Map<string, TaskDto[]>();
    for (const projectId of projectIndex.value.keys()) {
      map.set(projectId, listOf(projectId));
    }
    return map;
  });

  /** Read-only view: ownerId → resolved tasks (backward compat for direct map reads). */
  const byOwner = computed(() => {
    const map = new Map<string, TaskDto[]>();
    for (const ownerId of ownerIndex.value.keys()) {
      map.set(ownerId, listOfOwner(ownerId));
    }
    return map;
  });

  function listOf(projectId: string): TaskDto[] {
    const ids = projectIndex.value.get(projectId) ?? [];
    return ids
      .map((id) => byId.value.get(id))
      .filter((t): t is TaskDto => t !== undefined);
  }

  function isProjectLoaded(projectId: string): boolean {
    return projectIndex.value.has(projectId);
  }

  function listOfOwner(userId: string): TaskDto[] {
    const ids = ownerIndex.value.get(userId) ?? [];
    return ids
      .map((id) => byId.value.get(id))
      .filter((t): t is TaskDto => t !== undefined);
  }

  function getCached(id: string): TaskDto | undefined {
    return byId.value.get(id);
  }

  /** Lookup by route param — numeric public id or legacy UUID. */
  function getCachedByRef(ref: string): TaskDto | undefined {
    if (/^\d+$/.test(ref)) {
      const n = Number(ref);
      for (const t of byId.value.values()) {
        if (t.number === n) return t;
      }
      return undefined;
    }
    return getCached(ref);
  }

  function taskBelongsToOwner(task: TaskDto, userId: string): boolean {
    return task.assignedToId === userId || task.coAssigneeIds.includes(userId);
  }

  function writeById(task: TaskDto): void {
    const next = new Map(byId.value);
    next.set(task.id, task);
    byId.value = next;
  }

  function reconcileOwnerIndexes(task: TaskDto, prev: TaskDto | undefined): void {
    if (ownerIndex.value.size === 0) return;
    const next = new Map(ownerIndex.value);
    for (const [userId, ids] of next) {
      const belongs = taskBelongsToOwner(task, userId);
      const has = ids.includes(task.id);
      if (belongs) {
        next.set(userId, insertTaskIdSorted(ids, task, byId.value));
      } else if (has || (prev && taskBelongsToOwner(prev, userId))) {
        next.set(
          userId,
          ids.filter((id) => id !== task.id),
        );
      }
    }
    ownerIndex.value = next;
  }

  function upsertTask(task: TaskDto, opts?: { previous?: TaskDto }): void {
    const prev = opts?.previous ?? byId.value.get(task.id);
    writeById(task);

    const nextProject = new Map(projectIndex.value);

    if (prev?.projectId && prev.projectId !== task.projectId && nextProject.has(prev.projectId)) {
      nextProject.set(
        prev.projectId,
        (nextProject.get(prev.projectId) ?? []).filter((id) => id !== task.id),
      );
    }

    const ids = nextProject.get(task.projectId) ?? [];
    nextProject.set(task.projectId, insertTaskIdSorted(ids, task, byId.value));

    projectIndex.value = nextProject;
    reconcileOwnerIndexes(task, prev);
  }

  function removeTask(id: string): void {
    const nextById = new Map(byId.value);
    nextById.delete(id);
    byId.value = nextById;

    const nextProject = new Map(projectIndex.value);
    for (const [projectId, ids] of nextProject) {
      if (ids.includes(id)) {
        nextProject.set(
          projectId,
          ids.filter((i) => i !== id),
        );
      }
    }
    projectIndex.value = nextProject;

    const nextOwner = new Map(ownerIndex.value);
    for (const [ownerId, ids] of nextOwner) {
      if (ids.includes(id)) {
        nextOwner.set(
          ownerId,
          ids.filter((i) => i !== id),
        );
      }
    }
    ownerIndex.value = nextOwner;
  }

  function setProjectList(projectId: string, tasks: TaskDto[]): void {
    const nextById = new Map(byId.value);
    for (const t of tasks) nextById.set(t.id, t);
    byId.value = nextById;

    const nextProject = new Map(projectIndex.value);
    nextProject.set(
      projectId,
      tasks.map((t) => t.id),
    );
    projectIndex.value = nextProject;
  }

  function setOwnerList(ownerId: string, tasks: TaskDto[]): void {
    const nextById = new Map(byId.value);
    for (const t of tasks) nextById.set(t.id, t);
    byId.value = nextById;

    const nextOwner = new Map(ownerIndex.value);
    nextOwner.set(
      ownerId,
      tasks.map((t) => t.id),
    );
    ownerIndex.value = nextOwner;
  }

  /** Drop a cached project list so the next load refetches (pile send-to-project). */
  function clearProjectList(projectId: string): void {
    if (!projectIndex.value.has(projectId)) return;
    const next = new Map(projectIndex.value);
    next.delete(projectId);
    projectIndex.value = next;
  }

  async function loadByOwner(userId: string, force = false): Promise<void> {
    if (loadingOwners.value.has(userId)) return;
    if (ownerIndex.value.has(userId) && !force) return;
    loadingOwners.value.add(userId);
    try {
      const res = await api<{ tasks: TaskDto[] }>(
        `/api/tasks?ownerId=${encodeURIComponent(userId)}`,
      );
      setOwnerList(userId, res.tasks);
    } finally {
      loadingOwners.value.delete(userId);
    }
  }

  async function fetchOne(
    id: string,
  ): Promise<{ task: TaskDto; comments: TaskCommentDto[] }> {
    const res = await api<{ task: TaskDto; comments: TaskCommentDto[] }>(
      `/api/tasks/${encodeURIComponent(id)}`,
    );
    if (projectIndex.value.has(res.task.projectId)) {
      upsertTask(res.task);
    } else {
      writeById(res.task);
    }
    return res;
  }

  function apiProjectRef(projectId: string): string {
    const project = useProjectsStore().getByRef(projectId);
    return project ? projectApiParam(project) : projectId;
  }

  async function loadProject(projectId: string, force = false): Promise<void> {
    if (loadingProjects.value.has(projectId)) return;
    if (projectIndex.value.has(projectId) && !force) return;
    loadingProjects.value.add(projectId);
    try {
      const res = await api<{ tasks: TaskDto[] }>(
        `/api/tasks?projectId=${encodeURIComponent(apiProjectRef(projectId))}`,
      );
      setProjectList(projectId, res.tasks);
    } finally {
      loadingProjects.value.delete(projectId);
    }
  }

  async function create(payload: CreateTaskDto): Promise<TaskDto> {
    const created = await api<TaskDto>('/api/tasks', { method: 'POST', body: payload });
    upsertTask(created);
    return created;
  }

  /** Prevent double-click toggles racing the same task. */
  const togglingTasks = new Set<string>();

  async function update(id: string, payload: UpdateTaskDto): Promise<TaskDto> {
    const previous = getCached(id);
    const updated = await api<TaskDto>(`/api/tasks/${id}`, { method: 'PATCH', body: payload });
    upsertTask(updated, { previous });
    return updated;
  }

  async function toggleDone(task: TaskDto): Promise<TaskDto | null> {
    if (togglingTasks.has(task.id)) {
      return getCached(task.id) ?? task;
    }
    togglingTasks.add(task.id);
    try {
      const current = getCached(task.id) ?? task;
      if (!current.done) {
        const commentsStore = useCommentsStore();
        await commentsStore.loadTask(task.id);
        if (!taskHasUserComment(commentsStore.listOf(task.id))) {
          if (!window.confirm(TASK_CLOSE_NO_COMMENT_CONFIRM)) {
            return null;
          }
        }
      }
      return await update(task.id, { done: !current.done });
    } finally {
      togglingTasks.delete(task.id);
    }
  }

  /** Move a task to another project (drag-and-drop), fixing both project buckets. */
  async function moveToProject(task: TaskDto, toProjectId: string): Promise<TaskDto> {
    if (task.projectId === toProjectId) return task;
    const updated = await api<TaskDto>(`/api/tasks/${task.id}`, {
      method: 'PATCH',
      body: { projectId: toProjectId },
    });
    upsertTask(updated, { previous: task });
    return updated;
  }

  async function remove(task: TaskDto): Promise<void> {
    await api<void>(`/api/tasks/${task.id}`, { method: 'DELETE' });
    removeTask(task.id);
  }

  interface PendingDelete {
    task: TaskDto;
    timer: ReturnType<typeof setTimeout>;
    toastId: number;
  }
  let pendingDelete: PendingDelete | null = null;

  function restoreTask(task: TaskDto): void {
    upsertTask(task);
  }

  function flushPendingDelete(options?: { keepalive?: boolean }): void {
    if (!pendingDelete) return;
    clearTimeout(pendingDelete.timer);
    dismissToast(pendingDelete.toastId);
    const t = pendingDelete.task;
    pendingDelete = null;
    if (options?.keepalive) {
      apiDeleteKeepalive(`/api/tasks/${encodeURIComponent(t.id)}`);
      return;
    }
    void remove(t).catch(() => restoreTask(t));
  }

  async function scheduleRemove(task: TaskDto): Promise<void> {
    if (pendingDelete) flushPendingDelete();

    const snapshot = { ...task };
    removeTask(task.id);

    const timer = setTimeout(() => {
      const pending = pendingDelete;
      pendingDelete = null;
      if (!pending) return;
      dismissToast(pending.toastId);
      void remove(pending.task).catch(() => {
        restoreTask(pending.task);
        showToast('Не удалось удалить задачу', { variant: 'danger' });
      });
    }, 5000);

    const toastId = showToast('Задача удалена', {
      action: {
        label: 'Отменить',
        onClick: () => {
          if (!pendingDelete) return;
          clearTimeout(pendingDelete.timer);
          dismissToast(pendingDelete.toastId);
          restoreTask(pendingDelete.task);
          pendingDelete = null;
        },
      },
    });
    pendingDelete = { task: snapshot, timer, toastId };
  }

  async function duplicate(taskId: string, payload: DuplicateTaskDto): Promise<TaskDto> {
    const created = await api<TaskDto>(`/api/tasks/${taskId}/duplicate`, {
      method: 'POST',
      body: payload,
    });
    upsertTask(created);
    return created;
  }

  async function copyLastMonthTasks(projectId: string): Promise<CopyLastMonthTasksResponseDto> {
    const project = useProjectsStore().getByRef(projectId);
    const ref = project ? projectApiParam(project) : projectId;
    const res = await api<CopyLastMonthTasksResponseDto>(
      `/api/projects/${ref}/copy-last-month-tasks`,
      { method: 'POST', body: {} },
    );
    if (res.tasks.length > 0) {
      const nextById = new Map(byId.value);
      for (const t of res.tasks) nextById.set(t.id, t);
      byId.value = nextById;

      const currentIds = projectIndex.value.get(projectId) ?? [];
      const known = new Set(currentIds);
      const mergedIds = [...currentIds];
      for (const t of res.tasks) {
        if (!known.has(t.id)) mergedIds.push(t.id);
      }
      const nextProject = new Map(projectIndex.value);
      nextProject.set(projectId, mergedIds);
      projectIndex.value = nextProject;

      for (const t of res.tasks) reconcileOwnerIndexes(t, undefined);
    }
    return res;
  }

  async function updateAllocationMonth(id: string, monthKey: string): Promise<TaskDto> {
    const previous = getCached(id);
    const updated = await api<TaskDto>(`/api/tasks/${encodeURIComponent(id)}/month`, {
      method: 'PATCH',
      body: { monthKey },
    });
    upsertTask(updated, { previous });
    return updated;
  }

  async function loadAllocationMonthContext(force = false): Promise<AllocationMonthContextDto> {
    if (allocationMonthContext.value && !force) return allocationMonthContext.value;
    const res = await api<AllocationMonthContextDto>('/api/tasks/allocation-month-context');
    allocationMonthContext.value = res;
    return res;
  }

  if (typeof window !== 'undefined') {
    const flushOnUnload = (): void => flushPendingDelete({ keepalive: true });
    window.addEventListener('beforeunload', flushOnUnload);
    window.addEventListener('pagehide', flushOnUnload);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushOnUnload();
    });
  }

  return {
    byId,
    projectIndex,
    ownerIndex,
    byProject,
    byOwner,
    loadingProjects,
    loadingOwners,
    listOf,
    isProjectLoaded,
    listOfOwner,
    getCached,
    getCachedByRef,
    loadProject,
    loadByOwner,
    fetchOne,
    create,
    update,
    toggleDone,
    moveToProject,
    remove,
    scheduleRemove,
    duplicate,
    allocationMonthContext,
    loadAllocationMonthContext,
    updateAllocationMonth,
    copyLastMonthTasks,
    clearProjectList,
  };
});
